import React, { useState, useEffect } from "react";
import { Form, Button, Row, Col, InputGroup, Modal } from "react-bootstrap";
import { validateEnclosedField } from "../../utils/validators";
import ObjectFieldSelector from "../fieldselector/ObjectFieldSelector";
import WorkflowAttachmentSelector from "./WorkflowAttachmentSelector";
import ObjectConfigApi from "../../api/ObjectConfigApi";
import TemplateSearchDialog from "../watconnect/whatsapp_template/TemplateSearchDialog";
import DataApi from "../../api/DataApi";
export default function WorkflowSendWhatsApp({
  config = {},
  onChange,
  objectName,
  readOnly,
  selectedWhatsAppSetting = null, // Optional prop, will use sessionStorage if not provided
}) {
  // Get WhatsApp setting from config (for saved workflows), prop, localStorage, or sessionStorage
  const whatsAppSetting = config.selectedWhatsAppSetting || selectedWhatsAppSetting || localStorage.getItem('selectedWhatsAppSetting') || sessionStorage.getItem('selectedWhatsAppSetting') || null;

  const [parameters, setParameters] = useState(config.parameters || []);
  const [recipientError, setRecipientError] = useState("");
  const [showFieldSelector, setShowFieldSelector] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [attachmentRequired, setAttachmentRequired] = useState(false);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentError, setAttachmentError] = useState("");
  const [phoneFields, setPhoneFields] = useState([]);
  const [selectedTemplateName, setSelectedTemplateName] = useState("");

  const [fieldSelectorConfig, setFieldSelectorConfig] = useState({
    target: null,
    filterType: "all",
  });

  // Fetch phone fields when objectName changes
  useEffect(() => {
    const fetchPhoneFields = async () => {
      if (!objectName) {
        setPhoneFields([]);
        return;
      }

      try {
        const response = await ObjectConfigApi.fetchConfigObjectByNameWithFields(objectName);
        if (response.success && response.data?.fields) {
          // Filter to only phone data type fields
          const filteredFields = response.data.fields.filter(f => {
            const dataType = (f.data_type || '').toLowerCase();
            return dataType === 'phone';
          });
          setPhoneFields(filteredFields);
        } else {
          setPhoneFields([]);
        }
      } catch (error) {
        console.error("Error fetching phone fields:", error);
        setPhoneFields([]);
      }
    };

    fetchPhoneFields();
  }, [objectName]);

  useEffect(() => {
    setParameters(config.parameters || []);
  }, [config.parameters]);

  // Initialize WhatsApp setting in config if not already set
  useEffect(() => {
    // Only set if not already in config and we have a value from localStorage/sessionStorage
    if (!config.selectedWhatsAppSetting) {
      // Try to get from localStorage first (since user mentioned it's there), then sessionStorage
      const settingFromStorage = localStorage.getItem('selectedWhatsAppSetting') || sessionStorage.getItem('selectedWhatsAppSetting') || selectedWhatsAppSetting || null;
      if (settingFromStorage) {
        console.log('[WorkflowSendWhatsApp] Initializing selectedWhatsAppSetting in config:', settingFromStorage);
        onChange("selectedWhatsAppSetting", settingFromStorage);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Fetch template name when templateId exists (for editing existing workflows)
  // First try to use templateName from config, otherwise fetch it
  useEffect(() => {
    // If templateName is already in config, use it
    if (config.templateName) {
      setSelectedTemplateName(config.templateName);
      return;
    }

    // Otherwise, fetch it from API
    const fetchTemplateName = async () => {
      if (config.templateId && whatsAppSetting) {
        try {
          const dataApi = new DataApi('watconnect');
          const result = await dataApi.fetchById(
            `watconnect_template?business_number=${whatsAppSetting}`
          );

          if (result?.data?.data && Array.isArray(result.data.data)) {
            const template = result.data.data.find(
              t => (t.template_id === config.templateId || t.id === config.templateId ||
                    t.template_id?.toString() === config.templateId?.toString() ||
                    t.id?.toString() === config.templateId?.toString())
            );

            if (template) {
              const templateName = template.template_name || template.templatename || "";
              setSelectedTemplateName(templateName);
              // Store in config for future use
              if (!config.templateName) {
                onChange("templateName", templateName);
              }
            }
          }
        } catch (error) {
          console.error("Failed to fetch template name:", error);
        }
      } else if (!config.templateId) {
        // Clear template name if no template ID
        setSelectedTemplateName("");
      }
    };

    fetchTemplateName();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.templateId, config.templateName, whatsAppSetting]);

  const handleFieldChange = (field, value) => {
    if (field === "recipientField") {
      const error = validateEnclosedField(value);
      setRecipientError(error);
    }

    onChange(field, value);
  };

  const handleRecipientFieldChange = (e) => {
    const fieldName = e.target.value;
    if (fieldName) {
      // Format as {{fieldname}}
      const mergeField = `{{${fieldName}}}`;
      const error = validateEnclosedField(mergeField);
      setRecipientError(error);
      onChange("recipientField", mergeField);
    } else {
      setRecipientError("");
      onChange("recipientField", "");
    }
  };

  // Extract field name from merge syntax like "{{fieldname}}"
  const getSelectedRecipientFieldName = () => {
    if (config?.recipientField && typeof config.recipientField === 'string' && config.recipientField.includes('{{')) {
      const match = config.recipientField.match(/\{\{(.+?)\}\}/);
      return match ? match[1].trim() : null;
    }
    return null;
  };

  const handleTemplateSelect = (template) => {
    // Store template ID and name
    const templateId = template.id || template.template_id;
    const templateName = template.name || template.template_name || "";
    onChange("templateId", templateId);
    onChange("templateName", templateName); // Store template name in config for persistence
    if (whatsAppSetting) {
      onChange("selectedWhatsAppSetting", whatsAppSetting); // Store WhatsApp setting in config
    }
    setSelectedTemplateName(templateName);

    // Handle parameters if template has example_body_text or message_body with parameters
    // The template structure from TemplateSearchDialog includes the full template object
    if (template.example_body_text || template.message_body) {
      try {
        const bodyText = template.example_body_text || template.message_body || "";
        // Extract parameters from {{1}}, {{2}}, etc.
        const paramRegex = /\{\{(\d+)\}\}/g;
        const paramMatches = [];
        let match;
        while ((match = paramRegex.exec(bodyText)) !== null) {
          paramMatches.push(parseInt(match[1]));
        }

        if (paramMatches.length > 0) {
          const maxParam = Math.max(...paramMatches);
          const generatedParams = Array.from({ length: maxParam }, (_, index) => ({
            position: index + 1,
            value: "",
            isActive: true,
            label: `Parameter ${index + 1}`,
          }));

          setParameters(generatedParams);
          onChange("parameters", generatedParams);
        } else {
          setParameters([]);
          onChange("parameters", []);
        }
      } catch (err) {
        console.error("Failed to parse template parameters", err);
        setParameters([]);
        onChange("parameters", []);
      }
    } else {
      setParameters([]);
      onChange("parameters", []);
    }

    // Handle attachment requirement
    if (template.header === "IMAGE" || template.header_image_url) {
      setAttachmentRequired(true);
      setAttachmentError(""); // reset error
    } else {
      setAttachmentRequired(false);
      setAttachmentFile(null); // clear file if no longer needed
    }
  };

  const addParameter = () => {
    const newParam = { position: "", value: "", isActive: true };
    const updated = [...parameters, newParam];
    setParameters(updated);
    onChange("parameters", updated);
  };

  const updateParameter = (index, key, value) => {
    const updated = [...parameters];
    updated[index][key] = value;
    setParameters(updated);
    onChange("parameters", updated);
  };

  const removeParameter = (index) => {
    const updated = parameters.filter((_, idx) => idx !== index);
    setParameters(updated);
    onChange("parameters", updated);
  };

  const openFieldSelector = (target, filterType = "all") => {
    setFieldSelectorConfig({ target, filterType });
    setShowFieldSelector(true);
  };

  const handleFieldSelected = (field) => {
    if (field?.field_name) {
      const mergeField = `{{${field.field_name}}}`;
      console.log("mergeField: ", mergeField);
      console.log("fieldSelectorConfig.target: ", fieldSelectorConfig.target);
      if (fieldSelectorConfig.target === "recipient") {
        handleFieldChange("recipientField", mergeField);
      } else if (typeof fieldSelectorConfig.target === "number") {
        const currentValue = parameters[fieldSelectorConfig.target].value || "";
        updateParameter(
          fieldSelectorConfig.target,
          "value",
          currentValue + mergeField
        );
      }
    }
    setShowFieldSelector(false);
  };

  return (
    <>
      <Row>
        <Col>
          <Form.Group>
            <Form.Label>Recipient Field</Form.Label>
            <Form.Select
              value={getSelectedRecipientFieldName() || ""}
              onChange={handleRecipientFieldChange}
              isInvalid={!!recipientError}
              required={!readOnly}
              disabled={readOnly}
              style={{ fontSize: '0.95rem', padding: '0.5rem 0.75rem', minHeight: '38px' }}
            >
              <option value="">-Select Phone Field-</option>
              {phoneFields.map((field) => (
                <option key={field.field_name} value={field.field_name}>
                  {field.field_label || field.field_name}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {recipientError}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col>
          <Form.Group>
            <Form.Label>WhatsApp Template</Form.Label>
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="Select a template..."
                value={selectedTemplateName}
                readOnly
                required={!readOnly && !config.templateId}
                disabled={readOnly}
              />
              <Button
                variant="outline-secondary"
                onClick={() => setShowTemplateDialog(true)}
                disabled={readOnly || !whatsAppSetting}
                title={!whatsAppSetting ? "WhatsApp setting required" : "Search and select template"}
              >
                Select Template
              </Button>
            </InputGroup>
            {config.templateId && (
              <Form.Text className="text-muted">
                Template ID: {config.templateId}
              </Form.Text>
            )}
            {!whatsAppSetting && (
              <Form.Text className="text-danger">
                WhatsApp setting is required to select templates
              </Form.Text>
            )}
          </Form.Group>
        </Col>
        <Col>
          <Form.Group className="mt-2">
            <Form.Label>Is Active</Form.Label>
            <Form.Check
              type="checkbox"
              checked={config.isActive || false}
              onChange={(e) => handleFieldChange("isActive", e.target.checked)}
              disabled={readOnly}
            />
          </Form.Group>{" "}
        </Col>
      </Row>

      {/*<Button className="mt-2" onClick={addParameter} disabled={readOnly}>
        Add Parameter
      </Button>*/}
      {parameters?.map((param, index) => (
        <Row key={index} className="mt-2 align-items-center">
          <Col md={2}>
            <Form.Control
              type="number"
              placeholder="Position"
              value={param.position}
              onChange={(e) =>
                updateParameter(index, "position", e.target.value)
              }
              disabled={readOnly}
              readOnly={true}
            />
          </Col>

          <Col md={10}>
            <InputGroup>
              <Form.Control
                type="text"
                placeholder={param.label || "Value"}
                value={param.value}
                onChange={(e) =>
                  updateParameter(index, "value", e.target.value)
                }
                disabled={readOnly}
                required={!readOnly}
              />
              <Button
                variant="outline-primary"
                onClick={() => openFieldSelector(index, "all")}
                disabled={readOnly}
              >
                Insert Field
              </Button>
            </InputGroup>
          </Col>
        </Row>
      ))}

      {attachmentRequired && (
        <Row className="mt-3">
          <Col md={12}>
            <WorkflowAttachmentSelector
              config={config.attachmentConfig || {}}
              onChange={(field, value) =>
                handleFieldChange("attachmentConfig", {
                  ...config.attachmentConfig,
                  [field]: value,
                })
              }
              readOnly={readOnly}
            />
            <Form.Text className="text-muted">
              This template requires an image attachment. Please upload the
              required file.
            </Form.Text>
          </Col>
        </Row>
      )}

      <Modal
        show={showFieldSelector}
        onHide={() => setShowFieldSelector(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Select a Field</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ObjectFieldSelector
            objectName={objectName}
            multiSelect={false}
            onSelect={handleFieldSelected}
            fieldTypeFilter={fieldSelectorConfig.filterType}
          />
        </Modal.Body>
      </Modal>

      <TemplateSearchDialog
        show={showTemplateDialog}
        onHide={() => setShowTemplateDialog(false)}
        onSelect={handleTemplateSelect}
        selectedWhatsAppSetting={whatsAppSetting}
        selectedTemplateId={config.templateId}
      />
    </>
  );
}
